import React, { useState, useEffect, useMemo } from 'react';
import {
  Row, Col, Image, Button, Spin, message, Typography, Tag,
  Card, Space, Divider, Form, InputNumber, Switch, Select, Input,
  Table, Alert
} from 'antd';
import {
  ArrowLeftOutlined, CalculatorOutlined, EyeOutlined,
  ShoppingCartOutlined, ShopOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { dropshipperAPI, exchangeRateAPI, productAPI, externalStoreAPI } from '../api';
import countryList from 'react-select-country-list';
import FreightDetailsModal from './Utils/FreightDetailsModal';
import { normalizeMoney } from '../utils/money';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';

const { Title, Text } = Typography;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const AddToMyStore = () => {
  const navigate = useNavigate();
  const { product_id } = useParams();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryAction = searchParams.get('action');
  const queryStore = useMemo(() => {
    const platform = searchParams.get('platform');
    const shopDomain = searchParams.get('shop_domain');
    if (platform && shopDomain) {
      return { platform, shop_domain: shopDomain };
    }
    return null;
  }, [searchParams]);
  const stateStore = location.state?.syncStore;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [freightChannels, setFreightChannels] = useState([]);
  const [myCustomizations, setMyCustomizations] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [form] = Form.useForm();
  const [variantPrices, setVariantPrices] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedFreightId, setSelectedFreightId] = useState(null);
  const [selectedCustomizationId, setSelectedCustomizationId] = useState(null);
  const [profitRate, setProfitRate] = useState(30);
  const [shippingError, setShippingError] = useState('');
  const [freightDetailsVisible, setFreightDetailsVisible] = useState(false);
  const [selectedFreightForDetails, setSelectedFreightForDetails] = useState(null);
  const syncStore = stateStore || (queryAction === 'sync' ? queryStore : null);

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  };

  const dropshipperProductId =
    location.state?.dropshipperProductId ||
    searchParams.get('dp_id');
  const isSyncFlow = Boolean(syncStore && dropshipperProductId);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // 并行获取数据
      await Promise.all([
        fetchFreightChannels(),
        fetchMyCustomizations(),
        fetchExchangeRates(),
        fetchProduct(),
      ]);
    } catch (error) {
      
      message.error('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const fetchProduct = async () => {
    try {
      if (!product_id) {
        message.error('Product not found');
        navigate('/dropshipper/my-products');
        return;
      }
      const response = await productAPI.getByIdAuth(product_id);
      setSelectedProduct(response.data);

      // 初始化表单
      form.setFieldsValue({
        is_active: true,
        freight_id: undefined,
        customization_id: undefined,
        dest_country: undefined
      });
    } catch (error) {
      
      message.error('Failed to load product details');
      navigate('/dropshipper/my-products');
    }
  };

  const fetchFreightChannels = async () => {
    try {
      const response = await dropshipperAPI.getFreightChannels();
      setFreightChannels(response.data);
    } catch (error) {
      message.error('Failed to fetch freight channels');
    }
  };

  const fetchMyCustomizations = async () => {
    try {
      const response = await dropshipperAPI.getMyCustomizations();
      setMyCustomizations(response.data);
    } catch (error) {
      message.error('Failed to fetch customizations');
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await exchangeRateAPI.getAll();
      const rates = {};
      response.data.forEach(rate => {
        rates[rate.currency_code] = rate.rate_to_usd;
      });
      rates['USD'] = 1.0; // USD to USD is 1
      setExchangeRates(rates);
    } catch (error) {
      
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const updatedVariants = isSyncFlow
        ? copyVariantsWithNewPrices(selectedProduct.variants, variantPrices)
        : [];

      if (isSyncFlow) {
        const targetDpId = Number(dropshipperProductId);
        if (!targetDpId || !syncStore) {
          message.error('Missing sync information');
          setSubmitting(false);
          return;
        }
        const hasMissingPrice = updatedVariants.some(variant => variant.price === undefined || variant.price === null || variant.price === '');
        if (hasMissingPrice) {
          message.warning('Please enter prices for all variants before syncing');
          setSubmitting(false);
          return;
        }
        await externalStoreAPI.syncProduct({
          platform: syncStore.platform,
          shop_domain: syncStore.shop_domain,
          dropshipper_product_id: targetDpId,
          variants: updatedVariants,
          is_active: values.is_active,
          freight_id: values.freight_id,
          customization_id: values.customization_id || null
        });
        message.success(`Product synced to ${syncStore.shop_domain}`);
      } else {
        // 添加新产品
        await dropshipperAPI.addProduct({
          product_id: selectedProduct.id,
          freight_id: values.freight_id,
          customization_id: values.customization_id || null,
          is_active: values.is_active
        });
        message.success('Product saved to My Products successfully!');
      }

      navigate('/dropshipper/my-products');
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail);
      } else if (isSyncFlow) {
        message.error('Failed to sync product to your store');
      } else {
        message.error('Failed to add product to My Products');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 收集所有变体的辅助函数
  const collectAllVariants = (variantsData) => {
    if (!variantsData || !Array.isArray(variantsData)) {
      return [];
    }

    return variantsData.map((variant, index) => ({
      path: (variant.variant_path || []).join('.'),
      data: variant,
      displayName: (variant.variant_path || []).length > 0 ? (variant.variant_path || []).join(' - ') : 'Single Product'
    }));
  };

  // 复制variants并更新价格的辅助函数
  const copyVariantsWithNewPrices = (originalVariants, priceMap) => {
    if (!originalVariants || !Array.isArray(originalVariants)) {
      return [];
    }

    return originalVariants.map(variant => {
      const variantPath = (variant.variant_path || []).join('.');
      const newPrice = priceMap[variantPath] !== undefined ? priceMap[variantPath] : variant.price;
      return {
        ...variant,
        price: newPrice
      };
    });
  };

  // 汇率转换函数
  const convertToUSD = (amount, fromCurrency) => {
    const numericAmount = toNumber(amount);
    if (!fromCurrency || fromCurrency === 'USD') return numericAmount;
    const rate = toNumber(exchangeRates[fromCurrency.toUpperCase()]);
    if (!rate) return numericAmount;
    // rate_to_usd 表示 1 USD = X 目标货币，所以转换为USD需要除以这个值
    return numericAmount / rate;
  };

  // 计算运费函数
  const calculateFreightCost = (freightData, destCountry, weight) => {
    if (!freightData || !destCountry || !freightData[destCountry]) {
      return { cost: 0, error: `Cannot ship to ${destCountry}` };
    }

    const countryData = freightData[destCountry];
    if (!countryData.weight) {
      return { cost: 0, error: 'No weight configuration found' };
    }

    // 找到适合的重量段
    let suitableRange = null;
    for (const [weightStr, pricing] of Object.entries(countryData.weight)) {
      const weightLimit = parseInt(weightStr);
      if (weight <= weightLimit) {
        if (!suitableRange || weightLimit < parseInt(suitableRange[0])) {
          suitableRange = [weightStr, pricing];
        }
      }
    }

    if (!suitableRange) {
      const maxWeight = Math.max(...Object.keys(countryData.weight).map(w => parseInt(w)));
      return { cost: 0, error: `Weight (${weight}g) exceeds maximum (${maxWeight}g)` };
    }

    const pricing = suitableRange[1];
    const prePacket = parseFloat(pricing.pre_packet || 0);
    const preKg = parseFloat(pricing.pre_kg || 0);
    const currency = pricing.currency || 'USD';

    // 计算运费：每包裹费用 + 每公斤费用 * 重量(kg)
    const weightKg = weight / 1000.0;
    const totalCost = prePacket + (preKg * weightKg);

    // 转换为USD
    const costUSD = convertToUSD(totalCost, currency);
    return { cost: costUSD, error: null };
  };

  // 获取国家选项
  const countries = countryList();
  const countryOptions = countries.getData().map(country => ({
    value: country.value,
    label: country.label
  }));

  // 处理国家选择变化
  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    setShippingError('');

    if (countryCode && selectedFreightId) {
      validateShipping(selectedFreightId, countryCode);
    }
  };

  // 处理运费渠道变化
  const handleFreightChange = (freightId) => {
    setSelectedFreightId(freightId);
    setShippingError('');

    if (freightId && selectedCountry) {
      validateShipping(freightId, selectedCountry);
    }
  };

  // 处理定制选择变化
  const handleCustomizationChange = (customizationId) => {
    setSelectedCustomizationId(customizationId);
  };

  // 显示运费详情
  const handleViewFreightDetails = () => {
    const freight = freightChannels.find(f => f.id === selectedFreightId);
    if (freight) {
      setSelectedFreightForDetails(freight);
      setFreightDetailsVisible(true);
    }
  };

  // 验证运输能力
  const validateShipping = (freightId, countryCode) => {
    const freight = freightChannels.find(f => f.id === freightId);
    if (freight && freight.data && !freight.data[countryCode]) {
      const freightName = freight.name;
      const countryName = countryOptions.find(c => c.value === countryCode)?.label || countryCode;
      setShippingError(`Freight Channel ${freightName} can not ship to ${countryName}.`);
    } else {
      setShippingError('');
    }
  };

  // 计算所有variant的价格（包含运费和定制费用）
  const calculateVariantPricing = () => {
    if (!selectedProduct?.variants) {
      return [];
    }

    const freight = freightChannels.find(f => f.id === selectedFreightId);
    const customization = myCustomizations.find(c => c.id === selectedCustomizationId);

    const allVariants = collectAllVariants(selectedProduct.variants);
    return allVariants.map(variant => {
      const weight = toNumber(variant.data.weight || 100);
      const originalPrice = toNumber(variant.data.price || 0);

      // 计算运费
      let shippingCost = 0;
      let shippingError = null;
      if (selectedFreightId && selectedCountry) {
        if (freight) {
          const result = calculateFreightCost(freight.data, selectedCountry, weight);
          shippingCost = result.error ? 0 : toNumber(result.cost);
          shippingError = result.error;
        }
      }

      // 计算定制费用
      const customizationCost = customization ? toNumber(customization.price_prepacket) : 0;

      const totalPrice = originalPrice + shippingCost + customizationCost;

      return {
        ...variant,
        originalPrice,
        shippingCost,
        customizationCost,
        totalPrice,
        shippingError,
        yourPrice: variantPrices[variant.path] || ''
      };
    });
  };

  // 自动计算Your Prices
  const calculateYourPrices = () => {
    const variantPricing = calculateVariantPricing();
    const newPrices = {};

    variantPricing.forEach(variant => {
      const calculatedPrice = toNumber(variant.totalPrice) * (1 + profitRate / 100);
      newPrices[variant.path] = Math.round(calculatedPrice * 100) / 100; // 保留两位小数
    });

    setVariantPrices(prev => ({ ...prev, ...newPrices }));
    message.success(`Calculated prices with ${profitRate}% profit margin`);
  };

  const primaryButtonLabel = isSyncFlow ? 'Add to My Store' : 'Add to My Products';

  const primaryButtonStyle = isSyncFlow
    ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
    : { backgroundColor: '#2557ff', borderColor: '#2557ff' };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!selectedProduct) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={3}>Product not found</Title>
          <Button type="primary" onClick={() => navigate('/dropshipper/my-products')}>
            Back to My Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Page Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ marginRight: '16px' }}
          >
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: '#333' }}>
            {isSyncFlow ? 'Add Product to My Store' : 'Add Product to My Products'}
          </Title>
        </div>
      </div>

      {/* Main Content */}
      <Card
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
        }}
      >
        {/* Product Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            {selectedProduct?.images?.[0] && (
              <Image
                src={`${API_BASE_URL}/products/${selectedProduct.id}/images/${selectedProduct.images[0].id}`}
                width={80}
                height={80}
                style={{ objectFit: 'cover', marginRight: 16, borderRadius: 4 }}
              />
            )}
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 20, marginBottom: 4 }}>
                {selectedProduct?.name}
              </div>
              <div style={{ color: '#666', marginBottom: 4 }}>
                Category: {selectedProduct?.category?.name}
              </div>
              <div style={{ color: '#666' }}>
                {selectedProduct?.variants?.length || 0} variants available
              </div>
              <br />
              {isSyncFlow && syncStore && (
                <div style={{ color: '#666' }}>
                  Adding to your{' '}
                  <Tag 
                    icon={
                      syncStore.platform === 'shopify' ? <ShopifyIcon /> :
                      syncStore.platform === 'woocommerce' ? <WooCommerceIcon /> :
                      <ShopOutlined />
                    }
                    style={
                      syncStore.platform === 'woocommerce' ? { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' } :
                      syncStore.platform === 'shopify' ? { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' } :
                      { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' }
                    }
                  >
                    {syncStore.platform.toUpperCase()}
                  </Tag>
                  store{' '}
                  <a href={`https://${syncStore.shop_domain}`} target="_blank" rel="noopener noreferrer">
                    {syncStore.shop_domain}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          {/* Top Section: Freight, Customization, Country */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={8}>
              <Form.Item
                label="Freight Channel"
                name="freight_id"
                rules={[{ required: true, message: 'Please select a freight channel!' }]}
              >
                <Select
                  placeholder="Select freight channel"
                  showSearch
                  optionFilterProp="children"
                  onChange={handleFreightChange}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={(() => {
                    // 只显示当前选中商品支持的物流渠道
                    const supportedChannelIds = selectedProduct?.supported_freight_channels || [];
                    const availableChannels = freightChannels.filter(channel =>
                      supportedChannelIds.includes(channel.id)
                    );
                    return availableChannels.map(channel => ({
                      value: channel.id,
                      label: channel.name
                    }));
                  })()}
                />
              </Form.Item>
              {selectedFreightId && (
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={handleViewFreightDetails}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    View Details
                  </Button>
                </div>
              )}
            </Col>

            <Col span={8}>
              <Form.Item
                label="Customization (Optional)"
                name="customization_id"
              >
                <Select
                  placeholder="Select customization (optional)"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  onChange={handleCustomizationChange}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={myCustomizations.map(customization => ({
                    value: customization.id,
                    label: `${customization.customization_name} ($${normalizeMoney(customization.price_prepacket ?? '0')}/package)`
                  }))}
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Mostly Ship To"
                name="dest_country"
                validateStatus={shippingError ? 'error' : ''}
                help={shippingError && (
                  <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                    ⚠️ {shippingError}
                  </span>
                )}
              >
                <Select
                  placeholder="Select destination country"
                  showSearch
                  optionFilterProp="label"
                  onChange={handleCountryChange}
                  options={countryOptions}
                  status={shippingError ? 'error' : undefined}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Price Calculator Section */}
          {isSyncFlow && selectedProduct?.variants && (
            <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <CalculatorOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                <span style={{ fontWeight: 'bold', marginRight: 16, color: '#52c41a' }}>Price Calculator</span>
                <span style={{ marginRight: 8 }}>Profit Rate:</span>
                <InputNumber
                  value={profitRate}
                  onChange={setProfitRate}
                  min={0}
                  max={1000}
                  precision={0}
                  style={{ width: 80 }}
                />
                <span style={{ marginLeft: 4, marginRight: 16 }}>%</span>
                <Button
                  type="primary"
                  size="small"
                  onClick={calculateYourPrices}
                  icon={<CalculatorOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                  Calculate Your Prices
                </Button>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                This will automatically calculate your selling prices based on (Total Price + Profit Rate). You can still manually adjust individual prices below.
                {!selectedCountry && <span style={{ color: '#ff7a00' }}> Note: Select a destination country to include shipping costs in calculations.</span>}
              </div>
            </div>
          )}

          {/* Variants Table */}
          {isSyncFlow && selectedProduct?.variants && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12 }}>Variant Pricing Details:</h4>
              <Table
                dataSource={calculateVariantPricing()}
                rowKey="path"
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
                columns={[
                  {
                    title: 'SKU',
                    dataIndex: ['data', 'SKU'],
                    width: 120,
                    fixed: 'left'
                  },
                  {
                    title: 'Specifications',
                    dataIndex: 'displayName',
                    width: 150,
                    render: (text) => text === 'Single Product' ? '-' : text
                  },
                  {
                    title: 'Unit Price',
                    width: 100,
                    render: (_, record) => `$${normalizeMoney(record.originalPrice ?? '0')}`
                  },
                  {
                    title: 'Shipping Fee',
                    width: 110,
                    render: (_, record) => {
                      if (!selectedFreightId || !selectedCountry || record.shippingError) {
                        return <span style={{ color: '#999' }}>-</span>;
                      }
                      return `$${normalizeMoney(record.shippingCost ?? '0')}`;
                    }
                  },
                  {
                    title: 'Customization',
                    width: 120,
                    render: (_, record) => Number(record.customizationCost) > 0 ? (
                      `$${normalizeMoney(record.customizationCost ?? '0')}`
                    ) : (
                      <span style={{ color: '#999' }}>-</span>
                    )
                  },
                  {
                    title: 'Total Price',
                    width: 110,
                    render: (_, record) => (
                      <span style={{ fontWeight: 'bold' }}>
                        ${normalizeMoney(record.totalPrice ?? '0')}
                      </span>
                    )
                  },
                  {
                    title: 'Your Price',
                    width: 120,
                    fixed: 'right',
                    render: (_, record) => (
                      <InputNumber
                        size="small"
                        value={variantPrices[record.path] || undefined}
                        min={0}
                        precision={2}
                        placeholder="Enter price"
                        formatter={value => `$ ${value}`}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        onChange={(value) => {
                          setVariantPrices(prev => ({
                            ...prev,
                            [record.path]: value || 0
                          }));
                        }}
                        style={{ width: '100%' }}
                      />
                    )
                  }
                ]}
              />
            </div>
          )}

          <Divider />

          {/* Bottom Section: Active Status and Submit */}
          <Row justify="space-between" align="middle">
            <Col>
              <Form.Item
                label="Active Status"
                name="is_active"
                valuePropName="checked"
                initialValue={true}
                style={{ marginBottom: 0 }}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col>
              <Space>
                <Button size="large" onClick={() => navigate(-1)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={submitting}
                  icon={<ShoppingCartOutlined />}
                  style={{
                    ...primaryButtonStyle,
                    minWidth: '160px'
                  }}
                >
                  {primaryButtonLabel}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Freight Details Modal */}
      <FreightDetailsModal
        visible={freightDetailsVisible}
        onCancel={() => {
          setFreightDetailsVisible(false);
          setSelectedFreightForDetails(null);
        }}
        selectedFreight={selectedFreightForDetails}
        exchangeRates={exchangeRates}
        zIndex={1050}
      />
    </div>
  );
};

export default AddToMyStore;
